import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiSave, FiX } from "react-icons/fi";
import ContactModel, { ContactPayload } from "../../Models/ContactModel";

interface ContactFormProps {
	contact: ContactModel | null;
	onClose: () => void;
	onSave: (contact: ContactPayload) => Promise<void>;
}

const emptyContact: ContactPayload = {
	name: "",
	phone_number: "",
	email: "",
	company: "",
	job_description: "",
	note: "",
};

function ContactForm({ contact, onClose, onSave }: ContactFormProps): JSX.Element {
	const [submitError, setSubmitError] = useState("");
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<ContactPayload>({ defaultValues: emptyContact });

	useEffect(() => {
		reset(contact ? {
			name: contact.name,
			phone_number: contact.phone_number,
			email: contact.email ?? "",
			company: contact.company ?? "",
			job_description: contact.job_description ?? "",
			note: contact.note ?? "",
		} : emptyContact);
	}, [contact, reset]);

	const submit = async (data: ContactPayload) => {
		setSubmitError("");
		try {
			await onSave({
				...data,
				name: data.name.trim(),
				phone_number: data.phone_number.trim(),
				email: data.email.trim(),
				company: data.company.trim(),
				job_description: data.job_description.trim(),
				note: data.note.trim(),
			});
		} catch (error) {
			setSubmitError(
				typeof error === "string" ? error : "שמירת איש הקשר נכשלה. נסו שוב.",
			);
		}
	};

	return (
		<div className="contact-dialog-backdrop" role="presentation" onMouseDown={onClose}>
			<section
				className="contact-dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="contact-dialog-title"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<header className="contact-dialog__header">
					<div>
						<span>{contact ? "עדכון פרטים" : "איש קשר חדש"}</span>
						<h2 id="contact-dialog-title">
							{contact ? `עריכת ${contact.name}` : "הוספה לספר הטלפונים"}
						</h2>
					</div>
					<button type="button" onClick={onClose} aria-label="סגירת הטופס">
						<FiX aria-hidden="true" />
					</button>
				</header>

				<form className="contact-form" onSubmit={handleSubmit(submit)}>
					<div className="contact-form__grid">
						<label>
							<span>שם מלא *</span>
							<input
								autoFocus
								autoComplete="name"
								placeholder="לדוגמה: ישראל ישראלי"
								{...register("name", {
									required: "יש להזין שם",
									validate: (value) => value.trim().length > 0 || "יש להזין שם",
								})}
							/>
							{errors.name && <small>{errors.name.message}</small>}
						</label>

						<label>
							<span>מספר טלפון *</span>
							<input
								type="tel"
								dir="ltr"
								autoComplete="tel"
								placeholder="050-123-4567"
								{...register("phone_number", {
									required: "יש להזין מספר טלפון",
									pattern: {
										value: /^[+0-9 ()-]{7,20}$/,
										message: "יש להזין מספר טלפון תקין",
									},
								})}
							/>
							{errors.phone_number && <small>{errors.phone_number.message}</small>}
						</label>

						<label>
							<span>חברה</span>
							<input
								autoComplete="organization"
								placeholder="שם החברה"
								{...register("company")}
							/>
						</label>

						<label>
							<span>תפקיד</span>
							<input
								autoComplete="organization-title"
								placeholder="לדוגמה: אחראי מלאי"
								{...register("job_description")}
							/>
						</label>

						<label className="contact-form__wide">
							<span>אימייל</span>
							<input
								type="email"
								dir="ltr"
								autoComplete="email"
								placeholder="name@company.com"
								{...register("email", {
									pattern: {
										value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
										message: "יש להזין כתובת אימייל תקינה",
									},
								})}
							/>
							{errors.email && <small>{errors.email.message}</small>}
						</label>

						<label className="contact-form__wide">
							<span>הערה</span>
							<textarea
								rows={4}
								placeholder="מידע שימושי, שעות התקשרות, תחומי אחריות ועוד..."
								{...register("note")}
							/>
						</label>
					</div>

					{submitError && <p className="contact-form__error" role="alert">{submitError}</p>}

					<footer className="contact-form__actions">
						<button type="button" className="contact-button contact-button--secondary" onClick={onClose}>
							ביטול
						</button>
						<button type="submit" className="contact-button contact-button--primary" disabled={isSubmitting}>
							<FiSave aria-hidden="true" />
							{isSubmitting ? "שומר..." : contact ? "שמירת שינויים" : "הוספת איש קשר"}
						</button>
					</footer>
				</form>
			</section>
		</div>
	);
}

export default ContactForm;
